/* eslint-disable max-lines */
import * as React from "react";
import { values, mean } from "lodash";
import { Table } from "semantic-ui-react";
import { z } from "zod";

import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";
import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";

import { createValidator } from "not-valid";

import { useQuestionnaireContainerForSection } from "@teamprevent/common/questionnaires";

import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

interface Props {
    questionConfigs: CustomQuestionConfig[];
}

const calculateAverageMeasurement = (measurements: number[]): number => {
    const result = mean(measurements);
    return Math.round(result * 10) / 10;
};

const isWithinValidRange = (value: number): boolean => z.number().gte(30).lte(250).multipleOf(0.1).safeParse(value).success;;

const measurementValidator = createValidator((value: number) => isWithinValidRange(value), "Value must be between 30 and 250, with up to 1 decimal place");

const averageMeasurementQuestionId = questionIds.BideltoidMeasurementAverageMeasurement;

export const HealthSurveillanceConsultationBideltoidMeasurementSection: React.FunctionComponent<Props> = (props: Props) => {

    const { section: { questions }, updateQuestion, readonly } = useQuestionnaireContainerForSection(sections.BideltoidMeasurement.id);
    const questionsArray = values(questions);
    const measurement1 = getAnswerToQuestionWithId(questionsArray, questionIds.BideltoidMeasurementMeasurement1) as number ?? 0;
    const measurement2 = getAnswerToQuestionWithId(questionsArray, questionIds.BideltoidMeasurementMeasurement2) as number ?? 0;
    const measurement3 = getAnswerToQuestionWithId(questionsArray, questionIds.BideltoidMeasurementMeasurement3) as number ?? 0;
    const bideltoidMeasurementAverageMeasurementAnswer = getAnswerToQuestionWithId(questionsArray, averageMeasurementQuestionId) as number ?? 0;
    const bideltoidMeasurementAverageMeasurementQuestionText =  (getQuestionConfig(
        props.questionConfigs, averageMeasurementQuestionId)as CustomQuestionConfig).questionText;

    const updateQuestionAnswer = React.useCallback((questionId: string, answer: QuestionAnswer) => {
        updateQuestion({
            id: questionId,
            isValid: true,
            question: bideltoidMeasurementAverageMeasurementQuestionText ,
            answer
        });
    }, [bideltoidMeasurementAverageMeasurementQuestionText, updateQuestion]);

    React.useEffect(() =>
    {
        const average = calculateAverageMeasurement([measurement1, measurement2, measurement3]);

        if (average !== bideltoidMeasurementAverageMeasurementAnswer) {
            updateQuestionAnswer(averageMeasurementQuestionId, average);
        }
    },
    [measurement1, measurement2, measurement3, updateQuestionAnswer, bideltoidMeasurementAverageMeasurementAnswer]);

    return (
        <ContextAwareProgressBarQuestionnaireSection
            {...props}
            {...sections.BideltoidMeasurement}
        >
            {YesNoWithFollowUpQuestion({
                readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions
            })}
            {getAnswerToQuestionWithId(questionsArray, questionIds.BideltoidMeasurementUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell content="" />
                            <Table.HeaderCell content="" />
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement1) as CustomQuestionConfig)
                                    .questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement1)}
                                    noLabel
                                    hideDivider
                                    validators={[measurementValidator]}
                                    mandatory
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement2) as CustomQuestionConfig)
                                    .questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement2)}
                                    noLabel
                                    hideDivider
                                    validators={[measurementValidator]}
                                    mandatory
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement3) as CustomQuestionConfig)
                                    .questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementMeasurement3)}
                                    noLabel
                                    hideDivider
                                    validators={[measurementValidator]}
                                    mandatory
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell
                                content={(getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementAverageMeasurement) as CustomQuestionConfig)
                                    .questionText}
                            />
                            <Table.Cell>
                                <NumberQuestion
                                    readonly={true}
                                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BideltoidMeasurementAverageMeasurement)}
                                    noLabel
                                    hideDivider
                                />
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </>}
        </ContextAwareProgressBarQuestionnaireSection>
    );
};
